import { FC, useState } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import { useIntl } from 'dibs-react-intl';

import { sbRespRefineMenuFiltersMap } from '../SbRespRefineMenu/SbRespRefineMenuFilters/sbRespRefineMenuFiltersMap';
import {
    VALID_DIMENSION_FILTERS,
    CATEGORY,
    SALE,
    PRICE,
} from '../SbSharedRefineMenu/sbSharedRefineMenuConstants';
import { SbRespRefineTopFilters_itemSearch } from './__generated__/SbRespRefineTopFilters_itemSearch.graphql';
import { SbRespRefineTopFilters_user } from './__generated__/SbRespRefineTopFilters_user.graphql';
import { SbRespRefineTopFilters_viewer } from './__generated__/SbRespRefineTopFilters_viewer.graphql';
import { SbRespRefineFilterDropdown } from './SbRespRefineFilterDropdown';

import styles from './SbRespRefineTopFilters.scss';

const FILTER_LIMIT = 9;

type Props = {
    itemSearch: SbRespRefineTopFilters_itemSearch;
    viewer: SbRespRefineTopFilters_viewer;
    user: SbRespRefineTopFilters_user;
    currency: string;
    isClient: boolean;
};

const SbRespRefineTopFiltersComponent: FC<Props> = ({
    itemSearch,
    viewer,
    user,
    currency,
    isClient,
}) => {
    const {
        filters,
        appliedFilters,
    }: {
        filters: SbRespRefineTopFilters_itemSearch['filters'];
        appliedFilters: SbRespRefineTopFilters_itemSearch['appliedFilters'];
    } = itemSearch;
    const [currentOpenFilterName, setCurrentOpenFilterName] = useState(''); // ensures only one filter open at a time
    const intl = useIntl();
    let dimensionsDisplayed = false;
    const appliedFilterNames = new Set(appliedFilters?.map(f => f?.name));
    if (appliedFilterNames.has(SALE)) {
        appliedFilterNames.add(PRICE); // sale filter is displayed under price facet
    }
    if (!filters || !filters.length) {
        return null;
    }
    return (
        <div className={styles.container}>
            {filters
                .map((filter, idx) => {
                    if (!filter) {
                        return null;
                    }
                    const { values, totalCount, name, localizedFilterName } = filter;
                    if (!name) {
                        return null;
                    }
                    const { component: FilterComponent, title: unformattedIntlTitle } =
                        sbRespRefineMenuFiltersMap[name] || {};
                    const titleOverride =
                        !!unformattedIntlTitle && intl.formatMessage(unformattedIntlTitle);
                    const title = titleOverride || localizedFilterName;
                    if (!FilterComponent || !title) {
                        return null;
                    }
                    if (VALID_DIMENSION_FILTERS.includes(name)) {
                        if (dimensionsDisplayed) {
                            return null;
                        }
                        dimensionsDisplayed = true;
                    }
                    const isApplied = name.startsWith(CATEGORY)
                        ? false
                        : appliedFilterNames.has(name);
                    return (
                        <SbRespRefineFilterDropdown
                            title={title}
                            isApplied={isApplied}
                            key={idx}
                            filterName={name}
                            currentOpenFilterName={currentOpenFilterName}
                            setCurrentOpenFilterName={setCurrentOpenFilterName}
                        >
                            <FilterComponent
                                itemSearch={itemSearch}
                                user={user}
                                viewer={viewer}
                                values={values}
                                filter={filter}
                                filters={filters}
                                currency={currency}
                                isClient={isClient}
                                filterName={name}
                                totalCount={totalCount}
                            />
                        </SbRespRefineFilterDropdown>
                    );
                })
                .filter(Boolean)
                .slice(0, FILTER_LIMIT)}
        </div>
    );
};

export const SbRespRefineTopFilters = createFragmentContainer(SbRespRefineTopFiltersComponent, {
    itemSearch: graphql`
        fragment SbRespRefineTopFilters_itemSearch on ItemSearchQueryConnection {
            filters {
                ...SbRespRefineMenuFilterCategory_filters
                ...SbRespRefineMenuFilterDimensions_filters
                name
                values {
                    displayName
                    urlLabel
                    count
                    code
                    linkReference
                    linkable
                    hexCode
                    stats {
                        key
                        values
                    }
                }
                totalCount
                localizedFilterName
            }
            appliedFilters {
                name
            }
            ...SbRespRefineMenuFilterPrice_itemSearch
            ...SbSharedRefineMenuFilterStone_itemSearch
            ...SbSharedRefineMenuFilterMetal_itemSearch
            ...SbSharedRefineMenuFilterColor_itemSearch
            ...SbSharedRefineMenuFilterStyle_itemSearch
            ...SbSharedRefineMenuFilterOrigin_itemSearch
            ...SbSharedRefineMenuFilterPeriod_itemSearch
            ...SbRespRefineMenuFilterCategory_itemSearch
            ...SbRespRefineMenuFilterStoneCut_itemSearch
            ...SbSharedRefineMenuFilterCreator_itemSearch
            ...SbSharedRefineMenuFilterMaterial_itemSearch
            ...SbRespRefineMenuFilterDeliveryOptions_itemSearch
            ...SbSharedRefineMenuFilterItemType_itemSearch
            ...SbRespRefineMenuFilterDimensions_itemSearch
            ...SbRespRefineMenuFilterCaratWeight_itemSearch
            ...SbSharedRefineMenuFilterAvailability_itemSearch
            ...SbSharedRefineMenuFilterMatchingSets_itemSearch
            ...SbSharedRefineMenuFilterGender_itemSearch
        }
    `,
    user: graphql`
        fragment SbRespRefineTopFilters_user on User {
            ...SbRespRefineMenuFilterPrice_user
            ...SbRespRefineMenuFilterDimensions_user
        }
    `,
    viewer: graphql`
        fragment SbRespRefineTopFilters_viewer on Viewer {
            ...SbSharedRefineMenuFilterMetal_viewer
            ...SbSharedRefineMenuFilterStone_viewer
            ...SbSharedRefineMenuFilterOrigin_viewer
            ...SbSharedRefineMenuFilterCreator_viewer
            ...SbSharedRefineMenuFilterMaterial_viewer
            ...SbRespRefineMenuFilterDeliveryOptions_viewer
        }
    `,
});
